import { useCallback, useEffect, useState } from "react";
import { Archive, ListChecks } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const formatDay = (value) => (value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const ENDPOINTS = { provider_placements: "/provider-placements", testing_requests: "/testing-requests", client_tasks: "/client-tasks" };

export function ProviderWorkItemsPanel({ onNotice, onError, reloadKey }) {
  const [items, setItems] = useState([]);
  const [archiving, setArchiving] = useState(null);
  const [reason, setReason] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/provider-work");
      setItems(response.data);
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  async function archive(item) {
    try {
      await api.post(`${ENDPOINTS[item.collection]}/${item.id}/archive`, { reason });
      setArchiving(null);
      setReason("");
      onNotice(`${item.work_type} "${item.title}" archived. It no longer appears in ${item.provider_name}'s portal.`);
      await load();
    } catch (requestError) {
      onError(formatApiError(requestError));
    }
  }

  const visible = items.filter((item) => showCompleted || item.status !== "Completed");

  return (
    <section className="requests-table-panel provider-work-items" data-testid="provider-work-items-panel">
      <div className="section-heading">
        <div>
          <h2>Provider work items</h2>
          <p>{visible.length} shown · placements, evaluation requests and client tasks currently in provider portals</p>
        </div>
        <label className="provider-work-toggle"><input checked={showCompleted} data-testid="provider-work-show-completed" type="checkbox" onChange={(event) => setShowCompleted(event.target.checked)} /> Show completed</label>
      </div>
      <div className="provider-work-list">
        {visible.map((item) => (
          <article key={item.id} data-testid={`admin-work-${item.id}`}>
            <div>
              <strong>{item.title}</strong>
              <span>{item.work_type} · {item.provider_name} · {item.client_name}</span>
              <small>
                {item.status}{item.due_date ? ` · due ${formatDay(item.due_date)}` : ""}{item.start_date ? ` · ${formatDay(item.start_date)} — ${item.end_date ? formatDay(item.end_date) : "ongoing"}` : ""}
                {item.report ? ` · report: ${item.report.original_filename}` : ""}
              </small>
            </div>
            {archiving === item.id ? (
              <div className="provider-work-archive-confirm" data-testid={`archive-work-confirm-${item.id}`}>
                <input data-testid={`archive-work-reason-${item.id}`} maxLength={300} placeholder="Reason (optional)" value={reason} onChange={(event) => setReason(event.target.value)} />
                <button className="button button-secondary" data-testid={`archive-work-cancel-${item.id}`} type="button" onClick={() => { setArchiving(null); setReason(""); }}>Cancel</button>
                <button className="button button-primary" data-testid={`archive-work-submit-${item.id}`} type="button" onClick={() => archive(item)}><Archive size={14} /> Archive</button>
              </div>
            ) : (
              <button className="provider-profile-link" data-testid={`archive-work-${item.id}`} type="button" onClick={() => setArchiving(item.id)}><Archive size={14} /> Archive</button>
            )}
          </article>
        ))}
        {!visible.length && <p className="provider-empty-state" data-testid="provider-work-items-empty"><ListChecks size={14} /> No active provider work items.</p>}
      </div>
    </section>
  );
}
